import { PARTNER_TYPES } from "@shared/schema";
import type { Lender, Partner, PartnerCertificationEvent, PartnerType, PartnerTypeChangeEvent, WholesalerSyncRun } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { BadgeCheck, CheckCircle2, ChevronDown, ChevronLeft, ChevronUp, Edit2, Link2, Loader2, Mail, Phone, RefreshCw, ShieldAlert, StickyNote, UserCheck, UserX, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { renderPartnerTypeChangedEmail } from "@shared/partnerTypeChangedEmail";
import { formatPartnerCommission } from "@shared/fees";
import { AdminPartnerMessage, AdminPartnerThread } from "./types";
import { RealtorListingsPanel } from "./RealtorListingsPanel";

export function PartnerDetailView({ partner, onBack, onUpdated }: { partner: Partner; onBack: () => void; onUpdated: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    contactName: partner.contactName,
    companyName: partner.companyName,
    email: partner.email,
    phone: partner.phone || "",
    commissionAmount: partner.commissionAmount,
    commissionType: (partner.commissionType ?? "flat") as "flat" | "percent",
    premiumTier: partner.premiumTier,
    partnerType: partner.partnerType,
    notes: partner.notes || "",
    details: (partner.details as Record<string, unknown>) || {},
  });
  const [suspendDialog, setSuspendDialog] = useState<{ open: boolean; reason: string }>({ open: false, reason: "" });
  const [certRevokeDialog, setCertRevokeDialog] = useState<{ open: boolean; reason: string }>({ open: false, reason: "" });
  const [changeTypeDialog, setChangeTypeDialog] = useState<{ open: boolean; type: string; notify: boolean }>({ open: false, type: partner.partnerType, notify: true });
  const [selectedAdminThread, setSelectedAdminThread] = useState<number | null>(null);
  const [syncHistoryOpen, setSyncHistoryOpen] = useState(false);

  const syncRunsQuery = useQuery<WholesalerSyncRun[]>({
    queryKey: ["/api/admin/partners", partner.id, "sync-runs"],
    enabled: partner.partnerType === "wholesaler" && syncHistoryOpen,
    queryFn: async () => {
      const r = await fetch(`/api/admin/partners/${partner.id}/sync-runs?limit=20`, { credentials: "include" });
      if (!r.ok) {
        const text = await r.text().catch(() => "");
        throw new Error(text || `Failed to load sync runs (${r.status})`);
      }
      const data = await r.json();
      return Array.isArray(data) ? data : [];
    },
  });

  const partnerThreadsQuery = useQuery<AdminPartnerThread[]>({
    queryKey: ["/api/admin/partner-threads", partner.id],
    queryFn: () => fetch(`/api/admin/partner-threads?partnerId=${partner.id}`, { credentials: "include" }).then(r => r.json()),
  });

  const linkedLendersQuery = useQuery<Lender[]>({
    queryKey: ["/api/admin/lenders"],
    enabled: partner.partnerType === "lender",
  });
  const linkedLenders = (linkedLendersQuery.data ?? []).filter(l => l.partnerId === partner.id);

  const certEventsQuery = useQuery<PartnerCertificationEvent[]>({
    queryKey: ["/api/admin/partners", partner.id, "certification-events"],
    enabled: partner.partnerType === "realtor",
    queryFn: () => fetch(`/api/admin/partners/${partner.id}/certification-events`, { credentials: "include" }).then(r => r.json()),
  });

  const typeChangeEventsQuery = useQuery<PartnerTypeChangeEvent[]>({
    queryKey: ["/api/admin/partners", partner.id, "type-change-events"],
    queryFn: () => fetch(`/api/admin/partners/${partner.id}/type-change-events`, { credentials: "include" }).then(r => r.json()),
  });

  const partnerThreadMessagesQuery = useQuery<AdminPartnerMessage[]>({
    queryKey: ["/api/admin/partner-threads", selectedAdminThread, "messages"],
    enabled: selectedAdminThread !== null,
    queryFn: () => fetch(`/api/admin/partner-threads/${selectedAdminThread}/messages`, { credentials: "include" }).then(r => r.json()),
  });

  const typeLabel = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, any>) =>
      apiRequest("PATCH", `/api/admin/partners/${partner.id}`, data),
    onSuccess: () => {
      onUpdated();
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partners", partner.id, "certification-events"] });
      toast({ title: "Partner updated" });
    },
    onError: (err: any) => toast({ title: "Failed to update", description: parseErrorMessage(err, "Couldn't update. Please try again."), variant: "destructive" }),
  });

  const changeTypeMutation = useMutation({
    mutationFn: async ({ newType, notify }: { newType: string; notify: boolean }) =>
      apiRequest("PATCH", `/api/admin/partners/${partner.id}`, { partnerType: newType, needsReclassification: false, notifyPartner: notify }),
    onSuccess: (_data, vars) => {
      onUpdated();
      setChangeTypeDialog({ open: false, type: partner.partnerType, notify: true });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partners", partner.id, "type-change-events"] });
      toast({ title: "Partner type updated", description: vars.notify ? "Notification email sent to partner." : "Partner was not emailed." });
    },
    onError: (err: any) => toast({ title: "Failed to change type", description: parseErrorMessage(err, "Couldn't change type. Please try again."), variant: "destructive" }),
  });

  const suspendMutation = useMutation({
    mutationFn: async ({ suspended, suspensionReason }: { suspended: boolean; suspensionReason?: string }) =>
      apiRequest("PATCH", `/api/admin/partners/${partner.id}`, { suspended, suspensionReason }),
    onSuccess: () => {
      onUpdated();
      setSuspendDialog({ open: false, reason: "" });
      toast({ title: partner.suspended ? "Partner reactivated" : "Partner suspended" });
    },
    onError: (err: any) => toast({ title: "Failed", description: parseErrorMessage(err, "Something went wrong. Please try again."), variant: "destructive" }),
  });

  const resetTotpMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => apiRequest("POST", `/api/admin/partners/${partner.id}/reset-totp`),
    onSuccess: () => {
      onUpdated();
      toast({ title: "Two-factor reset", description: "Partner can re-enrol on next login." });
    },
    onError: (err: any) => toast({ title: "Failed to reset 2FA", description: parseErrorMessage(err, "Couldn't reset 2FA. Please try again."), variant: "destructive" }),
  });

  const details = (partner.details as Record<string, unknown>) || {};
  const detailStr = (key: string): string => { const v = details[key]; return typeof v === "string" ? v : ""; };
  const detailBool = (key: string): boolean => details[key] === true;
  const detailArr = (key: string): string[] => { const v = details[key]; return Array.isArray(v) ? v.map(String) : []; };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" className="h-8 px-2" onClick={onBack} data-testid="button-back-partners">
          <ChevronLeft className="h-4 w-4 mr-1" /> All Partners
        </Button>
        <div className="h-4 w-px bg-border" />
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">{typeLabel(partner.partnerType)}</Badge>
          {partner.suspended && <Badge variant="destructive" className="text-xs">Suspended</Badge>}
          {partner.premiumTier && <Badge className="text-xs bg-amber-500">Premium</Badge>}
          {partner.needsReclassification && <Badge variant="outline" className="text-xs text-amber-600 border-amber-400">Needs Reclassification</Badge>}
        </div>
        <div className="ml-auto flex gap-2">
          {!editing && (
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setChangeTypeDialog({ open: true, type: partner.partnerType, notify: true })} data-testid="button-change-partner-type">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Change Type
            </Button>
          )}
          {!editing && (
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEditing(true)} data-testid="button-edit-partner">
              <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
            </Button>
          )}
          {!editing && partner.totpEnabled && (
            <Button size="sm" variant="outline" className="h-8 text-xs"
              onClick={() => {
                if (confirm(`Reset two-factor authentication for ${partner.contactName}? They will be required to enrol again on their next login.`)) {
                  resetTotpMutation.mutate();
                }
              }}
              disabled={resetTotpMutation.isPending}
              data-testid="button-reset-partner-totp">
              {resetTotpMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ShieldAlert className="h-3.5 w-3.5 mr-1" />} Reset 2FA
            </Button>
          )}
          {partner.suspended ? (
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => suspendMutation.mutate({ suspended: false })} disabled={suspendMutation.isPending} data-testid="button-reactivate-partner">
              <UserCheck className="h-3.5 w-3.5 mr-1" /> Reactivate
            </Button>
          ) : (
            <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={() => setSuspendDialog({ open: true, reason: "" })} data-testid="button-suspend-partner">
              <UserX className="h-3.5 w-3.5 mr-1" /> Suspend
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4 space-y-4">
          <h3 className="font-semibold text-sm">Account Information</h3>
          {editing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Contact Name</Label>
                  <Input className="h-8 mt-0.5 text-sm" value={form.contactName} onChange={e => setForm(f => ({ ...f, contactName: e.target.value }))} data-testid="input-partner-contact-name" />
                </div>
                <div>
                  <Label className="text-xs">Company Name</Label>
                  <Input className="h-8 mt-0.5 text-sm" value={form.companyName} onChange={e => setForm(f => ({ ...f, companyName: e.target.value }))} data-testid="input-partner-company-name" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input className="h-8 mt-0.5 text-sm" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="input-partner-email" />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input className="h-8 mt-0.5 text-sm" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="input-partner-phone" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Commission</Label>
                  <div className="flex gap-1 mt-0.5">
                    <Select value={form.commissionType} onValueChange={v => setForm(f => ({ ...f, commissionType: v as "flat" | "percent" }))}>
                      <SelectTrigger className="h-8 text-sm w-24" data-testid="select-commission-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="flat">Flat ($)</SelectItem>
                        <SelectItem value="percent">Percent (%)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input className="h-8 text-sm flex-1" type="number" value={form.commissionAmount} onChange={e => setForm(f => ({ ...f, commissionAmount: e.target.value }))} data-testid="input-partner-commission" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Partner Type</Label>
                  <div className="h-8 mt-0.5 flex items-center gap-2 text-sm">
                    <Badge variant="outline" data-testid="text-partner-type">{typeLabel(form.partnerType)}</Badge>
                    <span className="text-xs text-muted-foreground">Use "Change Type" to reclassify.</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.premiumTier} onCheckedChange={v => setForm(f => ({ ...f, premiumTier: v }))} data-testid="switch-premium-tier" />
                <Label className="text-xs cursor-pointer">Premium Tier</Label>
              </div>
              <div>
                <Label className="text-xs">Internal Notes</Label>
                <Textarea className="mt-0.5 text-sm" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="input-partner-notes" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(false)}>Cancel</Button>
                <Button size="sm" className="h-7 text-xs" disabled={updateMutation.isPending} onClick={() => updateMutation.mutate({ ...form })} data-testid="button-save-partner">
                  {updateMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />} Save
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2"><span className="text-muted-foreground w-28 shrink-0">Contact</span><span className="font-medium" data-testid="text-partner-contact-name">{partner.contactName}</span></div>
              <div className="flex items-start gap-2"><span className="text-muted-foreground w-28 shrink-0">Company</span><span>{partner.companyName}</span></div>
              <div className="flex items-start gap-2"><Mail className="h-3.5 w-3.5 text-muted-foreground mt-0.5" /><span>{partner.email}</span></div>
              {partner.phone && <div className="flex items-start gap-2"><Phone className="h-3.5 w-3.5 text-muted-foreground mt-0.5" /><span>{partner.phone}</span></div>}
              <div className="flex items-start gap-2"><span className="text-muted-foreground w-28 shrink-0">Commission</span><span className="font-medium">
                {formatPartnerCommission(partner.commissionType, partner.commissionAmount)}
              </span></div>
              <div className="flex items-start gap-2"><span className="text-muted-foreground w-28 shrink-0">Premium Tier</span><span>{partner.premiumTier ? "Yes" : "No"}</span></div>
              {partner.notes && <div className="flex items-start gap-2"><StickyNote className="h-3.5 w-3.5 text-muted-foreground mt-0.5" /><span className="text-muted-foreground">{partner.notes}</span></div>}
              {partner.suspended && partner.suspensionReason && (
                <div className="rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2 text-destructive text-xs">
                  <strong>Suspension reason:</strong> {partner.suspensionReason}
                </div>
              )}
            </div>
          )}
        </Card>

        <Card className="p-4 space-y-4">
          <h3 className="font-semibold text-sm">Type-Specific Details</h3>

          {/* ── Read-only view ── */}
          {!editing && partner.partnerType === "lender" && (
            <div className="space-y-2 text-sm">
              <div className="flex gap-2"><span className="text-muted-foreground w-32 shrink-0">NMLS #</span><span data-testid="text-nmls">{detailStr("nmlsNumber") || "—"}</span></div>
              <div className="flex gap-2"><span className="text-muted-foreground w-32 shrink-0">States Licensed</span><span>{detailArr("statesLicensed").join(", ") || "—"}</span></div>
              <div className="flex gap-2"><span className="text-muted-foreground w-32 shrink-0">Loan Types</span><span>{detailArr("loanTypes").join(", ") || "—"}</span></div>
            </div>
          )}
          {!editing && partner.partnerType === "wholesaler" && (
            <div className="space-y-2 text-sm">
              <div className="flex gap-2"><span className="text-muted-foreground w-32 shrink-0">Markets</span><span>{detailArr("markets").join(", ") || "—"}</span></div>
              <div className="flex gap-2"><span className="text-muted-foreground w-32 shrink-0">Airtable Key</span><span>{detailStr("airtableKey") ? "Configured" : "—"}</span></div>
            </div>
          )}
          {!editing && partner.partnerType === "realtor" && (
            <div className="space-y-2 text-sm">
              <div className="flex gap-2"><span className="text-muted-foreground w-32 shrink-0">License #</span><span>{detailStr("licenseNumber") || "—"}</span></div>
              <div className="flex gap-2"><span className="text-muted-foreground w-32 shrink-0">License State</span><span>{detailStr("licenseState") || "—"}</span></div>
              <div className="flex gap-2"><span className="text-muted-foreground w-32 shrink-0">Co-living Deals</span><span data-testid="text-coliving-deals">{typeof details["colivingDealsCount"] === "number" ? details["colivingDealsCount"] : "—"}</span></div>
              <div className="flex items-center justify-between">
                <div className="flex gap-2"><span className="text-muted-foreground w-32 shrink-0">Co-living Certified</span>
                  {detailBool("colivingCertified") ? (
                    <Badge className="text-xs bg-green-600"><BadgeCheck className="h-3 w-3 mr-1" />Certified</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">Not certified</Badge>
                  )}
                </div>
                {detailBool("colivingCertified") ? (
                  <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => setCertRevokeDialog({ open: true, reason: "" })} data-testid="button-revoke-cert">
                    Revoke Certification
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="h-7 text-xs text-green-600" onClick={() => {
                    updateMutation.mutate({ details: { ...details, colivingCertified: true, certificationDate: new Date().toISOString(), certificationRevokeReason: null } });
                  }} data-testid="button-grant-cert">
                    Grant Certification
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* ── Editable view ── */}
          {editing && form.partnerType === "lender" && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">NMLS Number</Label>
                <Input className="h-8 mt-0.5 text-sm" placeholder="e.g. 123456"
                  value={typeof form.details["nmlsNumber"] === "string" ? form.details["nmlsNumber"] : ""}
                  onChange={e => setForm(f => ({ ...f, details: { ...f.details, nmlsNumber: e.target.value } }))}
                  data-testid="input-lender-nmls" />
              </div>
              <div>
                <Label className="text-xs">States Licensed <span className="text-muted-foreground">(comma-separated)</span></Label>
                <Input className="h-8 mt-0.5 text-sm" placeholder="e.g. CA, TX, FL"
                  value={Array.isArray(form.details["statesLicensed"]) ? (form.details["statesLicensed"] as string[]).join(", ") : ""}
                  onChange={e => setForm(f => ({ ...f, details: { ...f.details, statesLicensed: e.target.value.split(",").map(s => s.trim()).filter(Boolean) } }))}
                  data-testid="input-lender-states" />
              </div>
              <div>
                <Label className="text-xs">Loan Types <span className="text-muted-foreground">(comma-separated)</span></Label>
                <Input className="h-8 mt-0.5 text-sm" placeholder="e.g. Conventional, FHA, DSCR"
                  value={Array.isArray(form.details["loanTypes"]) ? (form.details["loanTypes"] as string[]).join(", ") : ""}
                  onChange={e => setForm(f => ({ ...f, details: { ...f.details, loanTypes: e.target.value.split(",").map(s => s.trim()).filter(Boolean) } }))}
                  data-testid="input-lender-loan-types" />
              </div>
            </div>
          )}
          {editing && form.partnerType === "wholesaler" && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Markets <span className="text-muted-foreground">(comma-separated)</span></Label>
                <Input className="h-8 mt-0.5 text-sm" placeholder="e.g. Atlanta, Birmingham"
                  value={Array.isArray(form.details["markets"]) ? (form.details["markets"] as string[]).join(", ") : ""}
                  onChange={e => setForm(f => ({ ...f, details: { ...f.details, markets: e.target.value.split(",").map(s => s.trim()).filter(Boolean) } }))}
                  data-testid="input-wholesaler-markets" />
              </div>
              <div>
                <Label className="text-xs">Airtable Key</Label>
                <Input className="h-8 mt-0.5 text-sm" placeholder="Airtable record key"
                  value={typeof form.details["airtableKey"] === "string" ? form.details["airtableKey"] : ""}
                  onChange={e => setForm(f => ({ ...f, details: { ...f.details, airtableKey: e.target.value } }))}
                  data-testid="input-wholesaler-airtable-key" />
              </div>
            </div>
          )}
          {editing && form.partnerType === "realtor" && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">License Number</Label>
                <Input className="h-8 mt-0.5 text-sm" placeholder="e.g. DRE-01234567"
                  value={typeof form.details["licenseNumber"] === "string" ? form.details["licenseNumber"] : ""}
                  onChange={e => setForm(f => ({ ...f, details: { ...f.details, licenseNumber: e.target.value } }))}
                  data-testid="input-realtor-license-number" />
              </div>
              <div>
                <Label className="text-xs">License State</Label>
                <Input className="h-8 mt-0.5 text-sm" placeholder="e.g. CA"
                  value={typeof form.details["licenseState"] === "string" ? form.details["licenseState"] : ""}
                  onChange={e => setForm(f => ({ ...f, details: { ...f.details, licenseState: e.target.value } }))}
                  data-testid="input-realtor-license-state" />
              </div>
              <div>
                <Label className="text-xs">Co-living Deals Completed</Label>
                <Input className="h-8 mt-0.5 text-sm" type="number" min="0" placeholder="e.g. 12"
                  value={typeof form.details["colivingDealsCount"] === "number" ? String(form.details["colivingDealsCount"]) : ""}
                  onChange={e => {
                    const val = e.target.value === "" ? undefined : Math.max(0, parseInt(e.target.value, 10));
                    setForm(f => ({ ...f, details: { ...f.details, colivingDealsCount: val } }));
                  }}
                  data-testid="input-realtor-coliving-deals" />
              </div>
              <p className="text-xs text-muted-foreground">Co-living certification is managed via Grant/Revoke buttons in view mode.</p>
            </div>
          )}

          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground">Joined: {partner.createdAt ? new Date(partner.createdAt).toLocaleDateString() : "—"}</p>
            {partner.sourceLenderId && <p className="text-xs text-muted-foreground">Migrated from lender #{partner.sourceLenderId}</p>}
            {partner.sourceBrokerId && <p className="text-xs text-muted-foreground">Migrated from broker #{partner.sourceBrokerId}</p>}
          </div>
        </Card>
      </div>

      {/* Listings with view counts (realtors only) */}
      {partner.partnerType === "realtor" && (
        <RealtorListingsPanel partnerId={partner.id} />
      )}

      {/* Type Change History (all partner types) */}
      <Card className="p-4 space-y-3" data-testid="card-type-change-history">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">Type Change History</h3>
          {typeChangeEventsQuery.isLoading && <span className="text-xs text-muted-foreground">Loading…</span>}
        </div>
        {!typeChangeEventsQuery.isLoading && (typeChangeEventsQuery.data ?? []).length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="text-no-type-change-history">No type changes recorded yet.</p>
        ) : (
          <ul className="divide-y text-sm">
            {(typeChangeEventsQuery.data ?? []).map((ev) => (
              <li key={ev.id} className="py-2 flex items-start gap-3" data-testid={`row-type-change-event-${ev.id}`}>
                <div className="shrink-0 mt-0.5">
                  <Badge variant="outline" className="text-[10px]">
                    <RefreshCw className="h-3 w-3 mr-1" />
                    {typeLabel(ev.fromType)} → {typeLabel(ev.toType)}
                  </Badge>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-muted-foreground" data-testid={`text-type-change-event-when-${ev.id}`}>
                    {new Date(ev.createdAt).toLocaleString()} · by {ev.adminLabel || (ev.adminUserId ? `admin #${ev.adminUserId}` : "admin")}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Certification History (realtors only) */}
      {partner.partnerType === "realtor" && (
        <Card className="p-4 space-y-3" data-testid="card-certification-history">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">Co-Living Certification History</h3>
            {certEventsQuery.isLoading && <span className="text-xs text-muted-foreground">Loading…</span>}
          </div>
          {!certEventsQuery.isLoading && (certEventsQuery.data ?? []).length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="text-no-cert-history">No certification changes recorded yet.</p>
          ) : (
            <ul className="divide-y text-sm">
              {(certEventsQuery.data ?? []).map((ev) => (
                <li key={ev.id} className="py-2 flex items-start gap-3" data-testid={`row-cert-event-${ev.id}`}>
                  <div className="shrink-0 mt-0.5">
                    {ev.action === "granted" ? (
                      <Badge className="text-[10px] bg-green-600"><BadgeCheck className="h-3 w-3 mr-1" />Granted</Badge>
                    ) : (
                      <Badge variant="destructive" className="text-[10px]">Revoked</Badge>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-muted-foreground" data-testid={`text-cert-event-when-${ev.id}`}>
                      {new Date(ev.createdAt).toLocaleString()} · by {ev.adminLabel || (ev.adminUserId ? `admin #${ev.adminUserId}` : "admin")}
                    </div>
                    {ev.action === "revoked" && ev.reason && (
                      <div className="mt-1 text-sm text-foreground" data-testid={`text-cert-event-reason-${ev.id}`}>
                        <span className="text-muted-foreground">Reason: </span>{ev.reason}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* Airtable Sync History (wholesalers only) */}
      {partner.partnerType === "wholesaler" && (
        <Card className="p-4 space-y-3" data-testid="card-sync-history">
          <button
            type="button"
            className="w-full flex items-center justify-between text-left"
            onClick={() => setSyncHistoryOpen(o => !o)}
            data-testid="button-toggle-sync-history"
          >
            <h3 className="font-semibold text-sm flex items-center gap-1.5">
              <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
              Recent Airtable Sync Runs
              {syncHistoryOpen && syncRunsQuery.isLoading && <span className="text-xs text-muted-foreground">(loading…)</span>}
            </h3>
            {syncHistoryOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </button>
          {syncHistoryOpen && (
            <>
              {syncRunsQuery.isLoading ? (
                <p className="text-xs text-muted-foreground">Loading sync history…</p>
              ) : (syncRunsQuery.data ?? []).length === 0 ? (
                <p className="text-xs text-muted-foreground" data-testid="text-no-sync-history">No sync runs recorded yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">When</TableHead>
                        <TableHead className="text-xs">Trigger</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                        <TableHead className="text-xs text-right">Total</TableHead>
                        <TableHead className="text-xs text-right">Created</TableHead>
                        <TableHead className="text-xs text-right">Updated</TableHead>
                        <TableHead className="text-xs text-right">Skipped</TableHead>
                        <TableHead className="text-xs text-right">Errors</TableHead>
                        <TableHead className="text-xs">Summary</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(syncRunsQuery.data ?? []).map((run) => (
                        <TableRow key={run.id} data-testid={`row-sync-run-${run.id}`}>
                          <TableCell className="text-xs whitespace-nowrap" title={new Date(run.startedAt).toLocaleString()}>
                            {new Date(run.startedAt).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-xs">
                            <Badge variant="outline" className="text-[10px] capitalize">{run.trigger}</Badge>
                          </TableCell>
                          <TableCell className="text-xs">
                            {run.success ? (
                              <Badge className="text-[10px] bg-green-600"><CheckCircle2 className="h-3 w-3 mr-1" />OK</Badge>
                            ) : (
                              <Badge variant="destructive" className="text-[10px]"><XCircle className="h-3 w-3 mr-1" />{run.status}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-right tabular-nums">{run.total}</TableCell>
                          <TableCell className="text-xs text-right tabular-nums">{run.created}</TableCell>
                          <TableCell className="text-xs text-right tabular-nums">{run.updated}</TableCell>
                          <TableCell className="text-xs text-right tabular-nums">{run.skipped + run.skippedInactive}</TableCell>
                          <TableCell className="text-xs text-right tabular-nums">{run.errorCount}</TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-md truncate" title={run.summary} data-testid={`text-sync-run-summary-${run.id}`}>{run.summary}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <p className="text-[10px] text-muted-foreground mt-2">Showing the latest {(syncRunsQuery.data ?? []).length} run{(syncRunsQuery.data ?? []).length === 1 ? "" : "s"}. Older runs beyond 50 are pruned automatically.</p>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* Login History */}
      {(() => {
        type LoginEntry = { timestamp: string; ip?: string; userAgent?: string };
        const loginHistory: LoginEntry[] = Array.isArray(partner.loginHistory) ? (partner.loginHistory as LoginEntry[]) : [];
        return (
          <Card className="p-4 space-y-3" data-testid="card-login-history">
            <h3 className="font-semibold text-sm">Login History</h3>
            {loginHistory.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="text-no-login-history">No logins recorded yet.</p>
            ) : (
              <div className="divide-y text-sm">
                {loginHistory.slice().reverse().slice(0, 20).map((entry, i) => (
                  <div key={i} className="py-1.5 flex items-start gap-4" data-testid={`row-login-history-${i}`}>
                    <span className="text-muted-foreground w-40 shrink-0 text-xs">{new Date(entry.timestamp).toLocaleString()}</span>
                    {entry.ip && <span className="text-xs text-muted-foreground">{entry.ip}</span>}
                    {entry.userAgent && <span className="text-xs text-muted-foreground truncate max-w-xs" title={entry.userAgent}>{entry.userAgent}</span>}
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })()}

      {partner.partnerType === "lender" && (
        <Card className="p-4 space-y-3" data-testid="card-linked-lenders">
          <h3 className="font-semibold text-sm flex items-center gap-1.5">
            <Link2 className="h-4 w-4" /> Linked Lender Cards
            {linkedLendersQuery.isLoading && <span className="text-xs text-muted-foreground">(loading…)</span>}
          </h3>
          {linkedLendersQuery.isLoading ? (
            <div className="space-y-2">{[1, 2].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : linkedLenders.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="text-no-linked-lenders">No Finance-tab lender cards are linked to this partner.</p>
          ) : (
            <div className="space-y-2">
              {linkedLenders.map(l => (
                <div key={l.id} className="flex items-center gap-2 rounded-md border px-3 py-2" data-testid={`row-linked-lender-${l.id}`}>
                  <span className={`w-2 h-2 rounded-full ${l.active ? "bg-green-400" : "bg-gray-300"}`} />
                  <div>
                    <p className="text-sm font-medium">{l.name} — {l.company}</p>
                    <p className="text-xs text-muted-foreground">{l.specialty}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Partner Threads (read-only) */}
      <Card className="p-4 space-y-3" data-testid="card-partner-threads">
        <h3 className="font-semibold text-sm flex items-center gap-1.5">
          Messages
          {partnerThreadsQuery.isLoading && <span className="text-xs text-muted-foreground">(loading…)</span>}
        </h3>
        {selectedAdminThread !== null ? (
          <div className="space-y-3">
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setSelectedAdminThread(null)}>
              ← Back to threads
            </Button>
            {partnerThreadMessagesQuery.isLoading ? (
              <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(partnerThreadMessagesQuery.data ?? []).map((msg) => (
                  <div key={msg.id} className={`flex gap-2 ${msg.senderType === "partner" ? "justify-end" : "justify-start"}`} data-testid={`msg-admin-${msg.id}`}>
                    <div className={`rounded-lg px-3 py-2 text-xs max-w-xs ${msg.senderType === "partner" ? "bg-blue-100 text-blue-900" : "bg-slate-100 text-slate-800"}`}>
                      <p className="font-medium text-[10px] text-muted-foreground mb-0.5">{msg.senderName}</p>
                      <p>{msg.content}</p>
                    </div>
                  </div>
                ))}
                {(partnerThreadMessagesQuery.data ?? []).length === 0 && (
                  <p className="text-xs text-muted-foreground">No messages yet.</p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {partnerThreadsQuery.isLoading ? (
              [1, 2].map(i => <Skeleton key={i} className="h-10 w-full" />)
            ) : (partnerThreadsQuery.data ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="text-no-partner-threads">No messages with this partner yet.</p>
            ) : (
              (partnerThreadsQuery.data ?? []).map((t) => (
                <button
                  key={t.id}
                  className="w-full text-left rounded-md border border-slate-200 px-3 py-2 hover:bg-slate-50 transition-colors"
                  onClick={() => setSelectedAdminThread(t.id)}
                  data-testid={`row-partner-thread-${t.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium truncate">{t.subject || t.threadType}</span>
                    {t.unreadByUser > 0 && <span className="text-[10px] bg-blue-100 text-blue-700 rounded-full px-1.5">{t.unreadByUser}</span>}
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate mt-0.5">{t.appUserName || "Anonymous"} · {t.lastMessageAt ? new Date(t.lastMessageAt).toLocaleDateString() : "—"}</p>
                </button>
              ))
            )}
          </div>
        )}
      </Card>

      {/* Change Partner Type Dialog */}
      <Dialog open={changeTypeDialog.open} onOpenChange={open => setChangeTypeDialog(d => ({ ...d, open, type: open ? d.type : partner.partnerType, notify: open ? d.notify : true }))}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-change-partner-type">
          <DialogHeader>
            <DialogTitle>Change Partner Type</DialogTitle>
            <DialogDescription>
              Manually reclassify <strong>{partner.contactName}</strong> ({partner.companyName}). The partner will immediately appear in the new portal and any "needs reclassification" flag will be cleared.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Current type</Label>
                <div className="mt-1 text-sm"><Badge variant="outline">{typeLabel(partner.partnerType)}</Badge></div>
              </div>
              <div>
                <Label className="text-xs">New type</Label>
                <Select value={changeTypeDialog.type} onValueChange={v => setChangeTypeDialog(d => ({ ...d, type: v }))}>
                  <SelectTrigger className="mt-1" data-testid="select-change-partner-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PARTNER_TYPES.map(t => <SelectItem key={t} value={t}>{typeLabel(t)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-md border p-3">
              <Switch
                id="notify-partner-toggle"
                checked={changeTypeDialog.notify}
                onCheckedChange={v => setChangeTypeDialog(d => ({ ...d, notify: v }))}
                data-testid="switch-notify-partner"
              />
              <div className="flex-1">
                <Label htmlFor="notify-partner-toggle" className="text-sm font-medium cursor-pointer">Notify partner by email</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  When on, the partner receives the email previewed below. Turn off for silent corrections (e.g. fixing a misclassification).
                </p>
              </div>
            </div>

            {changeTypeDialog.type !== partner.partnerType && PARTNER_TYPES.includes(changeTypeDialog.type as PartnerType) && (() => {
              const preview = renderPartnerTypeChangedEmail({
                contactName: partner.contactName,
                companyName: partner.companyName,
                fromType: partner.partnerType as PartnerType,
                toType: changeTypeDialog.type as PartnerType,
              });
              return (
                <div className={`space-y-2 ${changeTypeDialog.notify ? "" : "opacity-50"}`} data-testid="email-preview-partner-type-change">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Email preview</Label>
                    {!changeTypeDialog.notify && <span className="text-xs text-muted-foreground">Will not be sent</span>}
                  </div>
                  <div className="rounded-md border bg-muted/40 text-xs">
                    <div className="border-b px-3 py-2 space-y-0.5">
                      <div><span className="text-muted-foreground">To:</span> <span data-testid="text-preview-to">{partner.email}</span></div>
                      <div><span className="text-muted-foreground">Subject:</span> <span className="font-medium" data-testid="text-preview-subject">{preview.subject}</span></div>
                    </div>
                    <iframe
                      title="Partner type changed email preview"
                      srcDoc={preview.html}
                      sandbox=""
                      className="w-full h-72 bg-white rounded-b-md"
                      data-testid="iframe-preview-body"
                    />
                  </div>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeTypeDialog({ open: false, type: partner.partnerType, notify: true })} data-testid="button-cancel-change-partner-type">Cancel</Button>
            <Button
              disabled={changeTypeDialog.type === partner.partnerType || changeTypeMutation.isPending}
              onClick={() => changeTypeMutation.mutate({ newType: changeTypeDialog.type, notify: changeTypeDialog.notify })}
              data-testid="button-confirm-change-partner-type">
              {changeTypeMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Change Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={suspendDialog.open} onOpenChange={open => setSuspendDialog(d => ({ ...d, open }))}>
        <DialogContent data-testid="dialog-suspend-partner">
          <DialogHeader><DialogTitle>Suspend Partner</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Provide a reason for suspending <strong>{partner.contactName}</strong>.</p>
            <div>
              <Label className="text-xs">Reason <span className="text-destructive">*</span></Label>
              <Textarea value={suspendDialog.reason} onChange={e => setSuspendDialog(d => ({ ...d, reason: e.target.value }))} rows={3} className="mt-1" data-testid="input-suspend-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendDialog({ open: false, reason: "" })}>Cancel</Button>
            <Button variant="destructive" disabled={!suspendDialog.reason.trim() || suspendMutation.isPending}
              onClick={() => suspendMutation.mutate({ suspended: true, suspensionReason: suspendDialog.reason })}
              data-testid="button-confirm-suspend">
              {suspendMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Suspend Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Certification Revoke Dialog */}
      <Dialog open={certRevokeDialog.open} onOpenChange={open => setCertRevokeDialog(d => ({ ...d, open }))}>
        <DialogContent data-testid="dialog-revoke-cert">
          <DialogHeader><DialogTitle>Revoke Co-living Certification</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Provide a reason for revoking the co-living certification for <strong>{partner.contactName}</strong>.</p>
            <div>
              <Label className="text-xs">Reason <span className="text-destructive">*</span></Label>
              <Textarea value={certRevokeDialog.reason} onChange={e => setCertRevokeDialog(d => ({ ...d, reason: e.target.value }))} rows={3} className="mt-1" data-testid="input-cert-revoke-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCertRevokeDialog({ open: false, reason: "" })}>Cancel</Button>
            <Button variant="destructive" disabled={!certRevokeDialog.reason.trim() || updateMutation.isPending}
              onClick={() => {
                updateMutation.mutate({ details: { ...details, colivingCertified: false, certificationDate: null, certificationRevokeReason: certRevokeDialog.reason } });
                setCertRevokeDialog({ open: false, reason: "" });
              }}
              data-testid="button-confirm-revoke-cert">
              {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Revoke Certification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
